import { useCallback, useEffect, useState, useSyncExternalStore } from "react"
import { validate as isUuid } from "uuid"
import { store } from "@/core/store"
import type { TelegramPreviewMedia } from "@core/components/telegram/preview/telegramMessagePreview"
import type { Media } from "../interface/media"
import { mediaAccessOwner } from "../services/mediaAccessSession"
import { mediaApi } from "../services/publications/mediaApi"
import { currentPostBotSession, usePostBotSession } from "./usePostBotSession"

type Descriptor = Pick<TelegramPreviewMedia, "id" | "kind"> &
  Partial<TelegramPreviewMedia>
export const mediaDescriptor = (media: Media): TelegramPreviewMedia => ({
  id: media.id,
  kind: media.kind,
  name: media.originalFilename,
  size: media.size,
})

/** Render-only descriptors. Content/draft setters receive IDs, never this array.
 * Mounted consumers subscribe; hidden dialogs must pass visible=false. Metadata
 * is safe in RTK, while the access response deliberately bypasses it. */
export function useMediaPreview(
  ids: readonly string[],
  supplied: readonly Descriptor[] = [],
  visible = true,
): TelegramPreviewMedia[] {
  const session = usePostBotSession()
  const [metadata, setMetadata] = useState<{
    session: string | null
    values: Record<string, Media | null>
  }>({ session, values: {} })
  const [metadataRetry, setMetadataRetry] = useState(0)
  const known = new Map(supplied.map((item) => [item.id, item]))
  const activeKey = JSON.stringify(
    visible && session
      ? [
          ...new Set(
            ids.filter((id) => isUuid(id) && !known.get(id)?.previewUrl),
          ),
        ]
      : [],
  )
  const missingKey = JSON.stringify(
    visible && session
      ? [...new Set(ids.filter((id) => isUuid(id) && !known.has(id)))]
      : [],
  )
  const subscribe = useCallback(
    (notify: () => void) => {
      if (currentPostBotSession() !== session) return () => {}
      const stops = (JSON.parse(activeKey) as string[]).map((id) =>
        mediaAccessOwner.subscribe(id, notify),
      )
      return () => stops.forEach((stop) => stop())
    },
    [activeKey, session],
  )
  useSyncExternalStore(subscribe, mediaAccessOwner.getVersion)
  useEffect(() => {
    let current = true
    const requests = (JSON.parse(missingKey) as string[]).map((id) => {
      const request = store.dispatch(
        mediaApi.endpoints.getMedia.initiate(id, {
          forceRefetch: metadataRetry > 0,
        }),
      )
      void request.unwrap().then(
        (media) => {
          if (current && currentPostBotSession() === session)
            setMetadata((previous) => ({
              session,
              values: {
                ...(previous.session === session ? previous.values : {}),
                [id]: media,
              },
            }))
        },
        () => {
          if (current && currentPostBotSession() === session)
            setMetadata((previous) => ({
              session,
              values: {
                ...(previous.session === session ? previous.values : {}),
                [id]: null,
              },
            }))
        },
      )
      return request
    })
    return () => {
      current = false
      requests.forEach((request) => request.unsubscribe())
    }
  }, [missingKey, session, metadataRetry])
  return ids.map((id, index) => {
    const record =
      metadata.session === session ? metadata.values[id] : undefined
    const descriptor =
      known.get(id) ??
      (record
        ? mediaDescriptor(record)
        : { id, kind: "photo" as const, name: `Медиа ${index + 1}` })
    if (descriptor.previewUrl)
      return { ...descriptor, name: descriptor.name ?? `Медиа ${index + 1}` }
    const state = session ? mediaAccessOwner.getSnapshot(id) : undefined
    const url = known.has(id) || record ? state?.url : undefined
    const metadataFailed = !known.has(id) && record === null
    const retryAccess = mediaAccessOwner.retryFor(id)
    return {
      ...descriptor,
      name: descriptor.name ?? `Медиа ${index + 1}`,
      previewUrl: url,
      previewLoading:
        !metadataFailed &&
        !state?.error &&
        (state?.phase === "loading" ||
          state?.phase === "idle" ||
          (!known.has(id) && !record)),
      previewError:
        state?.error ??
        (metadataFailed ? "Не удалось загрузить предпросмотр" : undefined),
      onPreviewError: url
        ? () => {
            if (currentPostBotSession() === session)
              mediaAccessOwner.resourceError(id, url)
          }
        : undefined,
      onPreviewRetry: () => {
        if (currentPostBotSession() !== session) return
        if (metadataFailed) setMetadataRetry((value) => value + 1)
        retryAccess()
      },
    }
  })
}
